<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: What is this ledger worth as of a date I choose, and how did it get there?
  Answer:   the net worth figure for the selected date, then the trend that led to it.
  Context:  the range and currency the figures were computed for.
  Controls: the From and Date pickers, plus the Reports tab strip shared by this
            destination's three views.
-->
<script setup lang="ts">
import { EmptyState, ErrorState } from '@taler/ui'
import { formatLocalDate, parseLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNetWorthHistory } from '@/features/dashboard'
import {
  canViewReporting,
  DateStepper,
  NetWorthCard,
  NetWorthTrendChart,
  ReportTabs,
  useNetWorth,
  useNetWorthQueryState,
} from '@/features/reporting'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()
const today = () => formatLocalDate(todayUtc(systemClock))
const queryState = useNetWorthQueryState(today)
const { displayCurrency } = useDisplayCurrency()

const canView = computed(() => canViewReporting(session.can))
// ISO dates order lexically, so a plain comparison is the date comparison.
const rangeInverted = computed(() => {
  const from = queryState.from.value
  return from !== undefined && from > queryState.date.value
})

const netWorth = useNetWorth({
  ledgerId: () => session.activeLedger.value?.id,
  date: () => queryState.date.value,
  displayCurrency: () => displayCurrency.value,
  enabled: () => canView.value,
})

const series = useNetWorthHistory({
  ledgerId: () => session.activeLedger.value?.id,
  from: () => queryState.from.value,
  until: () => queryState.date.value,
  displayCurrency: () => displayCurrency.value,
  enabled: () => canView.value && !rangeInverted.value,
})

const hasNoPoints = computed(
  () => !rangeInverted.value && series.data.value !== undefined && series.data.value.points.length === 0,
)
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-6 p-[var(--space-page)]">
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('reporting.title') }}
    </h1>
    <ReportTabs />

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('reporting.noAccess') }}</p>

    <template v-else>
      <div class="flex flex-wrap items-end gap-4">
        <label
          class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          <span class="text-[color:var(--color-text-muted)]">{{ t('reporting.from') }}</span>
          <input
            type="date"
            :max="today()"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-2 py-1 text-[color:var(--color-text)]"
            :value="queryState.from.value ?? ''"
            :aria-invalid="rangeInverted || undefined"
            @change="queryState.setFrom(($event.target as HTMLInputElement).value)"
          />
        </label>
        <label
          class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          <span class="text-[color:var(--color-text-muted)]">{{ t('reporting.date') }}</span>
          <input
            type="date"
            :max="today()"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-2 py-1 text-[color:var(--color-text)]"
            :value="queryState.date.value"
            @change="queryState.setDate(($event.target as HTMLInputElement).value)"
          />
        </label>
        <DateStepper
          :model-value="queryState.date.value"
          :max="today()"
          @update:model-value="queryState.setDate($event)"
        />
      </div>
      <p v-if="rangeInverted" role="alert" class="text-[color:var(--color-text-muted)]">
        {{ t('reporting.netWorthChart.invalidRange') }}
      </p>

      <p v-if="netWorth.isLoading.value" role="status">{{ t('reporting.loading') }}</p>

      <ErrorState
        v-else-if="netWorth.error.value"
        :title="t('reporting.errorTitle')"
        :retry-label="t('reporting.retry')"
        :retrying="netWorth.retrying.value"
        @retry="netWorth.retry()"
      />

      <NetWorthCard
        v-else-if="netWorth.data.value"
        :value="netWorth.data.value.netWorth"
        :date="parseLocalDate(queryState.date.value)"
      />

      <p v-if="series.isLoading.value && !rangeInverted" role="status">{{ t('reporting.loading') }}</p>

      <ErrorState
        v-else-if="series.error.value && !rangeInverted"
        :title="t('reporting.errorTitle')"
        :retry-label="t('reporting.retry')"
        :retrying="series.retrying.value"
        @retry="series.retry()"
      />

      <EmptyState
        v-else-if="hasNoPoints"
        :title="
          queryState.from.value === undefined
            ? t('reporting.netWorthChart.emptyTitle')
            : t('reporting.netWorthChart.noMatchTitle')
        "
        :body="
          queryState.from.value === undefined
            ? t('reporting.netWorthChart.emptyBody')
            : t('reporting.netWorthChart.noMatchBody')
        "
      />

      <NetWorthTrendChart
        v-else-if="series.data.value && !rangeInverted"
        :points="series.data.value.points"
        :resolution-days="series.data.value.resolutionDays"
      />
    </template>
  </main>
</template>
